<template>
    <div class="col-12 mt-3">
        <div class="card-body px-0 py-0" ref="tosAcceptedContainer">
            <div class="relative row mb-2 checkbox-item">
                <div class="ml-4 ml-mobile-0 text-center">
                    <input
                        type="checkbox"
                        class="checkbox"
                        :class="productIssuerAccepted ? 'checkbox-error' : ''"
                        id="product_issuer_accepted"
                        name="product_issuer_accepted"
                        v-model="productIssuerAccepted"
                    />
                </div>
                <div class="col-12 col-lg-11 text-justify pr-0 checkbox-text">
                    <div class="checkbox-info-container">
                        <label class="smaller mb-0" for="product_issuer_accepted">
                            <span v-html="$t('shop.combivignette_product_issuer_checkbox')"></span>
                        </label>
                        <div
                            v-if="providersPopupEnabled"
                            class="ml-2 ml-lg-3"
                            @mouseover="showInfoBox = true"
                            @mouseleave="showInfoBox = false"
                            @click="showInfoBox = !showInfoBox"
                        >
                            <span class="mdi mdi-information-outline mdi-24px text-quote"></span>
                        </div>
                    </div>

                    <div class="invalid-feedback d-block" v-if="productIssuerError">
                        <span class="d-block">{{ $t('shop.pre-order-warning') }}</span>
                    </div>
                </div>

                <div
                    v-if="showInfoBox && providersPopupEnabled"
                    class="info-overlay p-4 row align-items-center bg-white rounded px-0 border-primary"
                >
                    <div class="justify-content-end w-100 show-mobile">
                        <span
                            class="mdi mdi-close mdi-24px cursor-pointer mr-6 text-quote"
                            @click="showInfoBox = false"
                        ></span>
                    </div>
                    <ul
                        class="infobox-list mobile-text-left text-quote"
                        v-html="$t('shop.combivignette_providers')"
                    ></ul>
                </div>
            </div>

            <div class="row mb-2 checkbox-item">
                <div class="ml-4 ml-mobile-0 text-center">
                    <input
                        type="checkbox"
                        class="checkbox"
                        :class="tosAccepted ? 'checkbox-error' : ''"
                        id="tos_accepted"
                        name="tos_accepted"
                        v-model="tosAccepted"
                    />
                </div>
                <div class="col-12 col-lg-11 text-justify pr-0 checkbox-text">
                    <label class="smaller mb-0" for="tos_accepted">
                        <span v-html="$t('shop.pre-order-notice')"></span>
                    </label>
                    <div class="invalid-feedback d-block" v-if="tosError">
                        <span class="d-block">{{ $t('shop.pre-order-warning') }}</span>
                    </div>
                </div>
            </div>

            <div class="row mb-2 checkbox-item">
                <div class="ml-4 ml-mobile-0 text-center">
                    <input
                        type="checkbox"
                        class="checkbox"
                        id="receive_newsletter"
                        name="receive_newsletter"
                        v-model="receiveNewsletter"
                    />
                </div>
                <div class="col-12 col-lg-11 text-justify pr-0 checkbox-text">
                    <label class="smaller mb-0" for="receive_newsletter">
                        <span v-html="$t('shop.receive-newsletter')"></span>
                        <span v-html="$t('shop.adsInfoText')"></span>
                        <span
                            @click.prevent="showUnsubscribeNewsletterModal()"
                            v-html="$t('shop.unsubscribeNewsletterShopBtn')"
                        >
                        </span>
                    </label>
                </div>
            </div>

            <div class="mb-4 ml-mobile-0 text-left">
                <span
                    class="notice-payment-method-delivery-period smaller mb-0"
                    v-html="$t('shop.additional_bank_transfer_info')"
                ></span>
            </div>

            <div class="row justify-content-center mb-mobile-3" ref="checkoutButton">
                <div class="col-12 col-md-5">
                    <div class="checkbox-button-container">
                        <button class="label btn btn-primary btn-block" @click="submitOrder" type="button">
                            <span class="fs-5">{{ $t('shop.order') }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <input type="hidden" name="product_type" id="product_type" :value="productType" />
        <input type="hidden" name="product_id" id="product_id" value="0" />
        <input type="hidden" name="option_id" id="option_id" value="0" />
        <input type="hidden" name="right_of_withdrawal" id="right_of_withdrawal" :value="rightOfWithdrawal" />
        <input type="hidden" name="voucher_code" id="voucher_code" :value="voucherCode" />

        <UnsubscribeNewsletterModal ref="unsubscribeNewsletterModal" :customer-email="email" />
        <ShopStatePopup ref="shopStatePopup" :email="email" />

        <loading-animation :visible="submitLoading" :full-page-loader="true"></loading-animation>

        <PaymentMethods
            class="payment-method-container same-margin-top offset-mobile-2"
            :small="true"
            @payment-method-changed="paymentMethod = $event"
            v-if="paymentMethodSelectionAvailable"
        ></PaymentMethods>
    </div>
</template>

<script>
import ShopStatePopup from '../Order/ShopStatePopup.vue';
import UnsubscribeNewsletterModal from '../Shop/UnsubscribeNewsletterModal.vue';
import PaymentMethods from '../PaymentMethods.vue';

export default {
    name: 'OrderFormControls',

    components: { PaymentMethods, UnsubscribeNewsletterModal, ShopStatePopup },

    props: ['userData', 'selectedProducts', 'productType', 'providersPopupEnabled', 'voucherCode'],

    data() {
        return {
            formErrors: {},
            rightOfWithdrawal: false,
            tosAccepted: false,
            productIssuerAccepted: false,
            receiveNewsletter: false,
            tosError: false,
            productIssuerError: false,
            submitLoading: false,
            showInfoBox: false,
            paymentMethodSelectionAvailable: true,
            paymentMethod: null,
        };
    },

    mounted() {
        window.addEventListener('click', this.handleClickOutside);
    },
    beforeUnmount() {
        window.removeEventListener('click', this.handleClickOutside);
    },

    computed: {
        email() {
            return this.userData?.email ?? '';
        },
    },

    methods: {
        showUnsubscribeNewsletterModal() {
            this.$refs.unsubscribeNewsletterModal.showModal();
        },

        getCombinedOrderFormData() {
            const form = new FormData(this.$parent.$parent.$refs.europamautOrderForm);

            const combinedData = [];
            this.selectedProducts.forEach((data) => {
                let chassisNumber = null;
                if (this.userData.chassisNumberRequired) {
                    chassisNumber = this.userData.chassisNumber;
                }

                combinedData.push({
                    system: data.product.system.key,
                    product_id: data.product.id,
                    option_id: data.option.id,
                    external_product_id: data.product.externalId,
                    external_option_id: data.option.externalId,
                    product_names: data.product.names,
                    validity_start: data.validity.from,
                    validity_end: data.validity.until,
                    chassis_number: chassisNumber,
                });
            });
            form.append('products', JSON.stringify(combinedData));

            return form;
        },

        handleFormErrors(errors) {
            if (typeof errors.products !== 'undefined') {
                errors.products.forEach((err) => {
                    try {
                        const productsError = JSON.parse(err);

                        if (productsError.some((obj) => 'overlap' in obj)) {
                            this.$emit('duration-error', productsError);
                        } else {
                            this.$emit('vehicle-error', productsError);
                        }
                    } catch (e) {
                        this.$emit('product-error');
                    }
                });
            }

            this.formErrors = errors;
            this.$emit('validation-failed', errors);
        },

        async submitOrder() {
            this.formErrors = {};
            this.$emit('submit-form');
            this.tosError = false;
            this.$forceUpdate();

            if (!this.tosAccepted) {
                this.tosError = true;

                const elementY = this.$refs.tosAcceptedContainer.getBoundingClientRect().top;
                const topNavHeight = document.querySelector('#topnav').getBoundingClientRect().height;
                const windowY = window.scrollY;

                window.scrollTo(0, windowY + elementY - topNavHeight);
                return;
            } else if (!this.productIssuerAccepted) {
                this.productIssuerError = true;

                const elementY = this.$refs.tosAcceptedContainer.getBoundingClientRect().top;
                const topNavHeight = document.querySelector('#topnav').getBoundingClientRect().height;
                const windowY = window.scrollY;

                window.scrollTo(0, windowY + elementY - topNavHeight);
                return;
            } else if (
                this.userData.chassisNumberRequired &&
                (!this.userData.chassisNumber || this.userData.chassisNumber.length < 17)
            ) {
                this.$emit('chassis-number-error');

                const elementY = document.querySelector('[for="chassis_number"]').getBoundingClientRect().top;
                const topNavHeight = document.querySelector('#topnav').getBoundingClientRect().height;
                const windowY = window.scrollY;

                window.scrollTo(0, windowY + elementY - topNavHeight);

                return;
            } else if (this.selectedProducts.length <= 0) {
                this.$emit('product-error');

                const elementY = document.querySelector('.flag-selection-wrapper').getBoundingClientRect().top;
                const topNavHeight = document.querySelector('#topnav').getBoundingClientRect().height;
                const windowY = window.scrollY;

                window.scrollTo(0, windowY + elementY - topNavHeight);
                return;
            }

            this.submitLoading = true;

            await axios
                .post('/' + this.$language + '/backend/validate/order', this.getCombinedOrderFormData())
                .then((response) => {
                    if (response.data.errors !== null) {
                        this.submitLoading = false;
                        this.handleFormErrors(response.data.errors);
                    } else if (response.status === 200 && response.data.shopActive === false) {
                        this.submitLoading = false;
                        this.$refs.shopStatePopup.showModal();
                    } else if (response.data.valid === true) {
                        this.createOrder();
                    }
                });
        },

        createOrder() {
            axios
                .post('/' + this.$language + '/payment/combined-order', this.getCombinedOrderFormData())
                .then((response) => {
                    this.paymentMethodSelectionAvailable = false;

                    if ('string' === typeof response.data.orderUri) {
                        window.location.href = response.data.orderUri;
                    } else {
                        this.submitLoading = false;
                    }
                })
                .catch(() => {
                    this.submitLoading = false;
                });
        },
        handleClickOutside(event) {
            const infoBox = this.$el.querySelector('.info-overlay');
            const infoIcon = this.$el.querySelector('.mdi-information-outline');

            if (
                this.showInfoBox &&
                infoBox &&
                !infoBox.contains(event.target) &&
                infoIcon &&
                !infoIcon.contains(event.target)
            ) {
                this.showInfoBox = false;
            }
        },
    },
};
</script>

<style scoped lang="scss">
.fs-5 {
    font-size: 1.25rem;
}

.invalid-feedback:not(:empty) {
    display: block;
}

.checkbox-item {
    margin-right: -20px !important;
}

@media (max-width: 767px) {
    .checkbox-item .checkbox-text {
        padding-right: 55px !important;
    }

    .notice-payment-method-delivery-period {
        display: block !important;
        margin-left: 35.5px !important;
    }
}

@media (min-width: 768px) and (max-width: 991px) {
    .checkbox-item .checkbox-text {
        padding-right: 80px !important;
    }

    .notice-payment-method-delivery-period {
        display: block !important;
        margin-left: 59.5px !important;
    }
}

@media (min-width: 992px) and (max-width: 1200px) {
    .checkbox-item .checkbox-text {
        padding-right: 5px !important;
    }

    .notice-payment-method-delivery-period {
        display: block !important;
        margin-left: 44.5px !important;
    }
}

@media (min-width: 1200px) {
    .checkbox-item .checkbox-text {
        margin-left: 15px !important;
    }

    .notice-payment-method-delivery-period {
        display: block !important;
        margin-left: 65px !important;
    }
}

@media (max-width: 1200px) {
    .checkbox-item {
        display: flex !important;
        align-items: flex-start;
        flex-wrap: nowrap !important;
    }

    .checkbox-item .checkbox-text {
        margin-top: 0;
        padding-left: 0;
        word-wrap: break-word;
        margin-left: 10px;
        text-align: justify !important;
    }
}

.checkbox-item .checkbox-text {
    margin-top: -5px;
    text-align: justify;
}

.notice-payment-method-delivery-period {
    text-align: justify !important;
}

.checkbox-info-container {
    display: flex !important;
    align-items: center;
}
.icon-primary-color {
    color: var(--main-color);
}
.infobox-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.info-overlay {
    position: absolute;
    top: 18%;
    right: 0.95rem;
    z-index: 1000;
}

.show-mobile {
    display: none;
}

@media (max-width: 992px) {
    .info-overlay {
        top: 19%;
    }
    .show-mobile {
        display: flex;
    }
}
</style>
